<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { deactivateCurrencyAction, getCurrenciesAction } from '@/api/functions/settings/get-currencies-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils'
import CreateCurrencyModal from './partials/CreateCurrencyModal.vue'
import SettingsOverview from './partials/SettingsOverview.vue'
import UpdateCurrencyModal from './partials/UpdateCurrencyModal.vue'

const showInactiveModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)
const toast = useToast()
const queryClient = useQueryClient()
const selectedCurrency = ref<null | { id: string }>(null)
const currentPage = ref(1)

// --- Query ---
const { data: currenciesData, refetch: refetchCurrencies } = useQueryApi({
  queryKey: computed(() => ['currencies', currentPage.value]), // reactive key
  queryFn: () => getCurrenciesAction(currentPage.value),
  // keepPreviousData: true,
})
const formatAmount = (val: string | number | null | undefined) => {
  if (val === null || val === undefined || val === '-') return '-'
  const num = Number(val)
  if (isNaN(num)) return '-'
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// --- Computed rows ---
const currencies = computed(() => {
  return (currenciesData.value?.data || []).map((item, idx) => ({
    ...item,
    index: idx + 1,
    sellPriceRange: item.margins?.selling ?? '-',
    buyPriceRange: item.margins?.buying ?? '-',
    currencyCode: item?.isoCode ?? '',
    statusLabel: item.status === 'ACTIVE' ? 'فعال' : 'غیرفعال',
    currentRate: formatAmount(item.exchange_rate),
    updateMode: item.auto_update_enabled ? 'خودکار' : 'دستی',
  }))
})

// --- Pagination data ---
const pagination = computed(() => currenciesData.value?.pagination)
const totalItems = computed(() => pagination.value?.total ?? 0)
const itemsPerPage = computed(() => pagination.value?.perPage ?? 10)

// --- Columns & actions ---
const columns = [
  { key: 'index', label: '#', class: 'col-span-1 font-fa text-sm' },
  { key: 'titleFa', label: 'ارز', class: 'col-span-1 text-sm' },
  { key: 'currencyCode', label: 'کد', class: 'col-span-1 text-[13px]' },
  { key: 'statusLabel', label: 'وضعیت', class: 'col-span-1 text-sm' },
  { key: 'currentRate', label: 'نرخ فعلی', class: 'col-span-1 font-fa text-sm' },
  { key: 'updateMode', label: 'نحوه بروزرسانی', class: 'col-span-1 text-xs' },
  {
    key: 'sellPriceRange',
    label: 'بازه قیمتی ثبت پیشنهاد روی فروش',
    class: 'col-span-2 font-fa text-sm',
    type: 'number',
  },
  {
    key: 'buyPriceRange',
    label: 'بازه قیمتی ثبت پیشنهاد روی خرید',
    class: 'col-span-2 font-fa text-sm',
    type: 'number',
  },
]
const actions = [
  { label: 'ویرایش ارز', handlerName: 'editCurrency' },
  { label: 'غیرفعال کردن', handlerName: 'deleteCurrency' },
]

// --- Handlers ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditCurrency = (currency: any) => {
  selectedCurrency.value = currency
  showUpdateModal.value = true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteCurrency = (currency: any) => {
  selectedCurrency.value = currency
  showInactiveModal.value = true
}

const deactivateMutation = useMutationApi({
  mutationFn: (id: string) => deactivateCurrencyAction(id),
  onSuccess: () => {
    toast.success('واحد پول با موفقیت غیرفعال شد')
    showInactiveModal.value = false
    selectedCurrency.value = null
    queryClient.invalidateQueries({ queryKey: ['currencies', currentPage.value] })
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleInactiveCurrency = () => {
  if (!selectedCurrency.value?.id) {
    toast.error('واحد پول انتخاب نشده است', 'خطا')
    return
  }
  deactivateMutation.mutate(selectedCurrency.value.id)
}

// --- Pagination handler ---
const goToPage = (page: number) => {
  currentPage.value = page
  refetchCurrencies()
}
</script>

<template>
  <div class="w-full mb-12">
    <ConfirmModal v-model="showInactiveModal" title="غیرفعال کردن واحد پول"
      :message="'آیا از غیرفعال کردن این واحد پول اطمینان دارید؟'" confirm-text="بله غیرفعال شود" cancel-text="بازگشت"
      @confirm="handleInactiveCurrency" @cancel="showInactiveModal = false" />

    <UpdateCurrencyModal v-model="showUpdateModal"
      :selectedCurrency="selectedCurrency as unknown as Record<string, string>" @confirm="refetchCurrencies"
      @cancel="showUpdateModal = false" />

    <CreateCurrencyModal v-model="showCreateModal" @confirm="refetchCurrencies" @cancel="showCreateModal = false" />

    <SettingsOverview :tabs="settingsTabs">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">
          <Button :isAdmin="true" @click="showCreateModal = true"
            class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
            افزودن ارز جدید
          </Button>
        </div>
      </template>
    </SettingsOverview>

    <div class="mt-20 lg:mt-7">
      <Table :columns="columns" :rows="currencies" :actions="actions" @primaryAction="onEditCurrency"
        @secondaryAction="onDeleteCurrency" />

      <!-- Pagination -->
      <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
        :totalItems="totalItems" :isAdmin="true" @update:currentPage="goToPage" />
    </div>
  </div>
</template>
